import { type Lang } from "@/lib/i18n";
import { type SubPageContent } from "@/components/common/SubPageLayout";

const MULTI_LAYER_IMAGE =
  "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-precision-s-cb8314.png";
const THREE_LAYER_IMAGE =
  "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_five_layer_mesh_structure.png";
const FIVE_LAYER_IMAGE =
  "https://mutualfilter.com/wp-content/uploads/2026/09/Sintered-Mesh-Filter-Elements-of-alloyfilter.jpg?v=hy0enjdkewc";
const MULTILAYER_HERO_IMAGE =
  "https://mutualfilter.com/wp-content/uploads/2026/09/Multi-Layer-Sintered-Woven-Wire-Mesh-Discs.jpg";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "MultiLayer",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Sintered Multilayer Mesh",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Multi Layer Sintered Mesh",
      lead: "Vacuum-sintered multi-layer wire mesh combined with rigid perforated plate or reinforcement mesh, built to endure heavy delta-P spikes and abrasive solid-liquid separation.",
      secondaryCta: {
        label: "Multilayer Overview",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Macro view of multi-layer sintered stainless steel wire mesh laminate",
      },
      cardNote: "Heavy Reinforcement Composite",
      cardLink: {
        label: "Request Specifications",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Filtration Rating", value: "1 – 200 µm" },
        { label: "Standard Alloys", value: "AISI 316L / 304L" },
        { label: "Layer Count", value: "2 to 9+ Layers" },
        { label: "Plate Thickness", value: "0.5 – 10.0 mm" },
      ],
    },
    overview: {
      eyebrow: "Heavy-Duty Architecture",
      heading: "Custom Multi-Layer Composites with Perforated Plate Backing",
      paragraphs: [
        "Multi-layer composite sintered wire mesh integrates multiple precision mesh layers with heavy reinforcing wire screens or solid perforated metal plates. Designed for process conditions involving severe dynamic pressures, viscous slurries, or abrasive bulk media, this construction achieves exceptional rigidity.",
        "When backed by a diffusion-bonded perforated metal plate, the thin filtration mesh is supported across thousands of micro-welded contact points. This design prevents flexure and fatigue failure under pulsating process loads while maintaining unobstructed straight-through flow paths.",
        "Custom configurations range from 2 layers up to 9 or more diffusion-bonded layers, with overall thicknesses spanning 0.5 mm up to 10.0 mm. Each assembly provides dimensional stability, long operational lifetimes, and full compatibility with backflushing cycles.",
      ],
      keyPoints: [
        "Vacuum diffusion bonding integrates fine woven mesh with heavy reinforcement or perforated plates.",
        "Withstands severe differential pressure fluctuations and abrasive particulate slurry streams.",
        "Custom layer counts from 2 to 9+ layers with total plate thicknesses from 0.5 to 10.0 mm.",
      ],
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Macro view of multi-layer sintered stainless steel wire mesh laminate",
      },
    },
    specTable: {
      heading: "Multi-Layer Composite Configurations",
      intro: "Available in AISI 316L and 304L stainless steels with custom wire diameter, weave type, and perforated plate thicknesses.",
      columns: [
        { key: "configuration", label: "Composite Type", align: "left" },
        { key: "layerRange", label: "Layers", align: "right" },
        { key: "ratingRange", label: "Rating Range (µm)", align: "right" },
        { key: "thicknessRange", label: "Thickness Range (mm)", align: "right" },
        { key: "baseSupport", label: "Support Base Type", align: "left" },
      ],
      rows: [
        {
          configuration: "Mesh + Perforated Plate Composite",
          layerRange: "2 – 4",
          ratingRange: "5 – 200",
          thicknessRange: "1.5 – 10.0",
          baseSupport: "Perforated Sheet (1.0 – 8.0 mm)",
        },
        {
          configuration: "6-Layer Heavy-Duty Matrix",
          layerRange: "6",
          ratingRange: "2 – 100",
          thicknessRange: "2.5 – 4.5",
          baseSupport: "Double heavy woven backing grids",
        },
        {
          configuration: "8-Layer Multi-Stage Laminate",
          layerRange: "8",
          ratingRange: "1 – 150",
          thicknessRange: "3.5 – 6.0",
          baseSupport: "Tri-mesh structural skeleton",
        },
        {
          configuration: "Ultra-Thin 2-Layer Laminate",
          layerRange: "2",
          ratingRange: "10 – 100",
          thicknessRange: "0.5 – 1.2",
          baseSupport: "Single fine support mesh",
        },
      ],
      caption: "All configurations engineered and diffusion-bonded to order based on system differential pressure requirements.",
    },
    features: {
      heading: "Engineered Performance Benefits",
      intro: "Tailored for heavy industrial applications requiring unmatched structural integrity and pore uniformity.",
      items: [
        {
          title: "Extreme Mechanical Rigidity",
          text: "Solid-state diffusion bonding to heavy wire grids or perforated metal plates prevents media deflection during sudden pressure spikes.",
        },
        {
          title: "Flexible Layer Customization",
          text: "Combine varying mesh weaves, wire diameters, and structural plates to meet exacting micron cut-offs and drainage flow rates.",
        },
        {
          title: "Abrasion and Fatigue Resistance",
          text: "Integrated metal substrates protect delicate filtration meshes against erosion in slurries, sand filtration, and polymer processing.",
        },
        {
          title: "Structural Fabrication Ready",
          text: "Compatible with CNC shearing, plasma cutting, edge machining, and automated TIG welding into conical, cylindrical, and plate assemblies.",
        },
      ],
    },
    related: {
      heading: "Explore Sintered Mesh Constructions",
      intro: "Compare multi-layer composites with standard 3-layer and 5-layer sintered mesh configurations.",
      cards: [
        {
          title: "Sintered Multilayer Mesh",
          text: "The parent family encompassing standard 3-layer, 5-layer, and multi-tier diffusion-bonded composite laminates.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Vacuum diffusion bonded sintered multilayer wire mesh laminate",
          },
          badge: "Parent Category",
          linkLabel: "View Multilayer Overview",
        },
        {
          title: "3 Layer Sintered Mesh",
          text: "Engineered with protective outer mesh, precision control layer, and open support grid for high flow permeability.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Cross section of 3-layer sintered stainless steel mesh laminate",
          },
          badge: "3-Layer",
          linkLabel: "View 3-Layer Specifications",
        },
        {
          title: "5 Layer Sintered Mesh",
          text: "The universal industrial configuration combining protection, control, dispersion, and dual skeleton layers for maximum stability.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "5-layer standard sintered wire mesh laminate structure",
          },
          badge: "5-Layer Standard",
          linkLabel: "View 5-Layer Specifications",
        },
      ],
    },
    cta: {
      id: "multi-layer-sintered-cta",
      eyebrow: "Custom Heavy Composites",
      heading: "Engineer Your Custom Multi-Layer Sintered Assembly",
      lead: "Our engineering team provides custom layering, perforated plate selection, and precision CNC fabrication to match your filtration rating and operating delta-P requirements.",
      secondaryLabel: "Contact Engineering Team",
      secondaryTo: "/ContactPage",
      note: "Manufactured in AISI 316L and 304L stainless steels. Certified chemical and physical data sheets available on order.",
    },
  },
  zh: {
    breadcrumbKey: "MultiLayer",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "多层烧结复合网",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "多层定制复合烧结网",
      lead: "通过真空高温扩散烧结工艺将多层不锈钢丝网与冲孔基板牢固结合，具备极高刚度与抗反洗脉冲寿命，专为抵抗严重压力尖峰与高磨损固液分离设计。",
      secondaryCta: {
        label: "多层烧结网总览",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "多层不锈钢烧结丝网微观复合结构与扩散焊接截面",
      },
      cardNote: "重载冲孔复合板",
      cardLink: {
        label: "获取技术参数",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "过滤精度", value: "1 – 200 µm" },
        { label: "标准材质", value: "AISI 316L / 304L" },
        { label: "复合层数", value: "2 至 9+ 层可选" },
        { label: "厚度范围", value: "0.5 – 10.0 mm" },
      ],
    },
    overview: {
      eyebrow: "重型工业结构",
      heading: "多层丝网与冲孔基板真空扩散复合技术",
      paragraphs: [
        "多层定制复合烧结网是将数层不同目数的编织丝网与加厚骨架网或刚性不锈钢冲孔板在高温真空环境下复合烧结。针对存在瞬时压差冲击、高粘度浆料循环或颗粒磨蚀性极强的重工况而专门研发。",
        "在冲孔板复合结构中，微米级控制丝网通过数以万计的冶金扩散焊点紧密固着在坚实冲孔板上。这种设计在介质产生脉冲逆洗与交变载荷时，能彻底消除丝网振颤疲劳，并保持畅通直达的流体排放通道。",
        "复合层数可根据实际工程需求在 2 层至 9 层及以上灵活配置，成品总厚度跨越 0.5 mm 至 10.0 mm，兼具稳定的微米截留能力与卓越的抗机械变形寿命。",
      ],
      keyPoints: [
        "真空扩散烧结将微细编织网与重型粗目网或坚固冲孔板结合为一体。",
        "能够承受恶劣的工况压差波动、剧烈脉冲反洗以及高磨蚀性浆料冲刷。",
        "层数支持 2 至 9+ 层定制，整体复合板厚度范围覆盖 0.5 至 10.0 mm。",
      ],
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "多层不锈钢烧结丝网微观复合结构与扩散焊接截面",
      },
    },
    specTable: {
      heading: "多层定制复合网典型参数",
      intro: "材质提供 AISI 316L 及 304L，支持根据工作压差与通量需求定制丝径、网型与冲孔板开孔率。",
      columns: [
        { key: "configuration", label: "复合构型类别", align: "left" },
        { key: "layerRange", label: "层数范围", align: "right" },
        { key: "ratingRange", label: "过滤精度 (µm)", align: "right" },
        { key: "thicknessRange", label: "厚度范围 (mm)", align: "right" },
        { key: "baseSupport", label: "基底支撑形式", align: "left" },
      ],
      rows: [
        {
          configuration: "丝网 + 冲孔板复合板",
          layerRange: "2 – 4",
          ratingRange: "5 – 200",
          thicknessRange: "1.5 – 10.0",
          baseSupport: "刚性冲孔板 (1.0 – 8.0 mm)",
        },
        {
          configuration: "6 层重载复合矩阵板",
          layerRange: "6",
          ratingRange: "2 – 100",
          thicknessRange: "2.5 – 4.5",
          baseSupport: "双层重型编织骨架网",
        },
        {
          configuration: "8 层多阶深层复合板",
          layerRange: "8",
          ratingRange: "1 – 150",
          thicknessRange: "3.5 – 6.0",
          baseSupport: "三层高强度刚性网组",
        },
        {
          configuration: "超薄 2 层微阻复合网",
          layerRange: "2",
          ratingRange: "10 – 100",
          thicknessRange: "0.5 – 1.2",
          baseSupport: "单层细目承载支撑网",
        },
      ],
      caption: "所有复合规格均按需工程化设计，根据介质粘度与压差要求确定开孔率与层级搭配。",
    },
    features: {
      heading: "核心工程性能优势",
      intro: "专为严苛工业过滤工况量身打造，兼顾结构刚度与微孔精度稳定性。",
      items: [
        {
          title: "极高的抗压抗爆破刚度",
          text: "与加厚冲孔板固相熔接后，在高压差尖峰和反向清洗冲击下完全不发生介质挠曲与网丝断裂。",
        },
        {
          title: "高度灵活的层级定制",
          text: "可按需搭配不同编织形式、丝径及孔隙开孔率，兼顾高截留精度与优异的流通阻力性能。",
        },
        {
          title: "耐磨蚀与抗疲劳寿命长",
          text: "坚固的基底有效分散颗粒介质的高速冲刷动能，保护核心控制网长期平稳运行。",
        },
        {
          title: "结构件深加工成型便利",
          text: "适于大型数控剪切、等离子切割、边缘机械加工及精密焊接成滤盘、滤板及圆锥体滤芯。",
        },
      ],
    },
    related: {
      heading: "烧结复合网全系构型对比",
      intro: "了解 3 层、5 层标准板与多层重型复合网的技术差异，匹配最合理的工程预算与性能。",
      cards: [
        {
          title: "多层烧结丝网总览",
          text: "涵盖 3 层、5 层及多层冲孔复合网在内的全系列高温真空固相扩散焊接产品。",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "高温真空扩散焊接多层烧结不锈钢丝网复合板",
          },
          badge: "上级系列",
          linkLabel: "查看多层烧结网总览",
        },
        {
          title: "3 层烧结丝网",
          text: "由保护网、微米级精滤网与重型结构支撑网构成，兼顾高通量渗透性能与可靠的机械抗压能力。",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "三层烧结不锈钢丝网微观截面与结构层析",
          },
          badge: "3 层高通量",
          linkLabel: "查看 3 层规格",
        },
        {
          title: "5 层标准烧结网",
          text: "工业通用基准结构，融合保护层、控制层、分流缓冲层与双层骨架，具备极高的结构稳定性。",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "五层标准不锈钢烧结丝网复合板",
          },
          badge: "5 层工业标准",
          linkLabel: "查看 5 层规格",
        },
      ],
    },
    cta: {
      id: "multi-layer-sintered-cta",
      eyebrow: "重型结构设计与加工",
      heading: "获取多层定制复合烧结网方案",
      lead: "互拓技术团队可根据您的系统压差、流体粘度及颗粒特性，协助完成层级配比与冲孔板结构选型，提供成品组件及整板供货。",
      secondaryLabel: "技术工程团队咨询",
      secondaryTo: "/ContactPage",
      note: "材质提供 AISI 316L、304L，提供完整的材质理化数据与力学测试记录（按订单要求）。",
    },
  },
  es: {
    breadcrumbKey: "MultiLayer",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Malla multicapa sinterizada",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malla compuesta multicapa sinterizada",
      lead: "Malla metálica multicapa sinterizada al vacío combinada con chapa perforada rígida o mallas de refuerzo, diseñada para soportar picos severos de presión diferencial.",
      secondaryCta: {
        label: "Resumen multicapa",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Vista macro de laminado de malla de acero inoxidable multicapa sinterizado",
      },
      cardNote: "Compuesto con chapa perforada",
      cardLink: {
        label: "Solicitar especificaciones",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Retención", value: "1 – 200 µm" },
        { label: "Aleaciones estándar", value: "AISI 316L / 304L" },
        { label: "Capas", value: "2 a 9+ capas" },
        { label: "Espesor", value: "0.5 – 10.0 mm" },
      ],
    },
    overview: {
      eyebrow: "Arquitectura reforzada",
      heading: "Compuestos multicapa a medida con respaldo de placa perforada",
      paragraphs: [
        "La malla compuesta multicapa integra varias capas de malla de precisión con telas de refuerzo pesadas o chapas perforadas sólidas. Concebida para procesos con fuertes presiones dinámicas o medios abrasivos, ofrece una rigidez insuperable.",
        "Al unirse por difusión a una chapa perforada, la malla filtrante queda fijada en miles de puntos micro-soldados, evitando la flexión y la fatiga del material bajo cargas cíclicas de retrolavado.",
        "Las combinaciones a medida van desde 2 hasta más de 9 capas, con espesores finales de 0.5 mm a 10.0 mm, garantizando estabilidad dimensional y compatibilidad plena con procesos de limpieza química o inversa.",
      ],
      keyPoints: [
        "La unión por difusión en alto vacío combina mallas finas con chapas perforadas rígidas.",
        "Soporta fluctuaciones severas de presión diferencial y flujos de lodos altamente abrasivos.",
        "Número de capas configurable de 2 a 9+ capas con espesores de 0.5 a 10.0 mm.",
      ],
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Vista macro de laminado de malla de acero inoxidable multicapa sinterizado",
      },
    },
    specTable: {
      heading: "Configuraciones compuestas multicapa",
      intro: "Disponibles en AISI 316L y 304L con diámetros de hilo y patrones de chapa perforada adaptados al caudal.",
      columns: [
        { key: "configuration", label: "Tipo de compuesto", align: "left" },
        { key: "layerRange", label: "Capas", align: "right" },
        { key: "ratingRange", label: "Retención (µm)", align: "right" },
        { key: "thicknessRange", label: "Espesor (mm)", align: "right" },
        { key: "baseSupport", label: "Soporte de base", align: "left" },
      ],
      rows: [
        {
          configuration: "Malla + Chapa perforada",
          layerRange: "2 – 4",
          ratingRange: "5 – 200",
          thicknessRange: "1.5 – 10.0",
          baseSupport: "Chapa perforada rígida (1.0 – 8.0 mm)",
        },
        {
          configuration: "Matriz pesada de 6 capas",
          layerRange: "6",
          ratingRange: "2 – 100",
          thicknessRange: "2.5 – 4.5",
          baseSupport: "Doble malla tejida de soporte",
        },
        {
          configuration: "Laminado profundo de 8 capas",
          layerRange: "8",
          ratingRange: "1 – 150",
          thicknessRange: "3.5 – 6.0",
          baseSupport: "Esqueleto estructural de triple malla",
        },
        {
          configuration: "Laminado fino de 2 capas",
          layerRange: "2",
          ratingRange: "10 – 100",
          thicknessRange: "0.5 – 1.2",
          baseSupport: "Malla única de soporte ligero",
        },
      ],
      caption: "Todas las configuraciones se diseñan y unen por difusión según los requerimientos de la aplicación.",
    },
    features: {
      heading: "Ventajas de rendimiento técnico",
      intro: "Diseñado para aplicaciones industriales que requieren integridad estructural y uniformidad de poro.",
      items: [
        {
          title: "Rigidez mecánica extrema",
          text: "La unión en estado sólido evita la deformación de la malla ante picos de presión diferencial.",
        },
        {
          title: "Personalización completa",
          text: "Combine diferentes tejidos y espesores de placa para alcanzar el corte micrométrico deseado.",
        },
        {
          title: "Resistencia a la abrasión",
          text: "El soporte metálico absorbe los impactos en corrientes de lodos, protegiendo las mallas más finas.",
        },
        {
          title: "Apta para conformado",
          text: "Permite corte por plasma, mecanizado de bordes y soldadura TIG para crear cilindros, conos y placas.",
        },
      ],
    },
    related: {
      heading: "Otras configuraciones sinterizadas",
      intro: "Compare las opciones compuestas reforzadas con los modelos de 3 y 5 capas.",
      cards: [
        {
          title: "Malla multicapa sinterizada",
          text: "Familia principal que abarca construcciones estándar y personalizadas unidas por difusión.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Laminado de malla de alambre multicapa sinterizado al vacío",
          },
          badge: "Familia principal",
          linkLabel: "Ver resumen multicapa",
        },
        {
          title: "Malla sinterizada de 3 capas",
          text: "Estructurada con malla protectora, control de precisión y soporte para alta permeabilidad.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Sección transversal de malla sinterizada de 3 capas",
          },
          badge: "3 capas",
          linkLabel: "Ver detalles de 3 capas",
        },
        {
          title: "Malla sinterizada estándar de 5 capas",
          text: "La configuración universal que combina protección, control, dispersión y refuerzo dual.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "Estructura de malla sinterizada estándar de 5 capas",
          },
          badge: "5 capas estándar",
          linkLabel: "Ver detalles de 5 capas",
        },
      ],
    },
    cta: {
      id: "multi-layer-sintered-cta",
      eyebrow: "Compuestos pesados a medida",
      heading: "Configure su conjunto multicapa reforzado",
      lead: "Nuestro equipo de ingeniería diseña laminados a medida con chapas perforadas para satisfacer sus condiciones operativas exactas.",
      secondaryLabel: "Consultar con ingenieros",
      secondaryTo: "/ContactPage",
      note: "Fabricación en AISI 316L y 304L con certificados de calidad disponibles bajo pedido.",
    },
  },
  de: {
    breadcrumbKey: "MultiLayer",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Mehrlagen-Sintergewebe",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Mehrlagen-Verbundsintergewebe",
      lead: "Vakuumgesintertes Mehrlagen-Drahtgewebe kombiniert mit fester Lochplatte oder Verstärkungsgewebe, ausgelegt für hohe Differenzdruckspitzen und abrasive Fest-Flüssig-Trennung.",
      secondaryCta: {
        label: "Mehrlagen-Übersicht",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Makroansicht eines mehrlagigen gesinterten Edelstahldrahtgewebes",
      },
      cardNote: "Lochblech-Verbundplatte",
      cardLink: {
        label: "Spezifikationen anfragen",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Feinheit", value: "1 – 200 µm" },
        { label: "Standardlegierungen", value: "AISI 316L / 304L" },
        { label: "Lagenanzahl", value: "2 bis 9+ Lagen" },
        { label: "Dicke", value: "0.5 – 10.0 mm" },
      ],
    },
    overview: {
      eyebrow: "Hochbelastbare Struktur",
      heading: "Kundenspezifische Mehrlagenverbunde mit Lochblechverstärkung",
      paragraphs: [
        "Mehrlagen-Verbundsintergewebe kombinieren feinmaschige Filtergewebe mit schweren Stützdrähten oder massiven Edelstahllochblechen. Entwickelt für Prozessbedingungen mit hohen Druckstößen oder zähflüssigen Medien, bieten sie unübertroffene Steifigkeit.",
        "Durch die Diffusionsverbindung mit einer stabilen Lochplatte wird das feine Kontrollgewebe an tausenden Schweißpunkten gestützt. Dies verhindert Durchbiegungen und Ermüdungsbrüche unter pulsierenden Betriebs- und Rückspülbelastungen.",
        "Kundenspezifische Aufbauten reichen von 2 bis zu über 9 Lagen bei Plattendicken von 0.5 mm bis 10.0 mm, mit dauerhafter Formtreue und vollständiger Eignung für zyklische CIP-Reinigungen.",
      ],
      keyPoints: [
        "Vakuum-Diffusionsglühen verbindet Feingewebe mit schweren Stützlagen oder Lochblechen.",
        "Widersteht extremen Differenzdruckschwankungen und abrasiven Partikelströmen.",
        "Individuelle Schichtanzahl von 2 bis 9+ Lagen mit Plattenstärken von 0.5 bis 10.0 mm.",
      ],
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Makroansicht eines mehrlagigen gesinterten Edelstahldrahtgewebes",
      },
    },
    specTable: {
      heading: "Konfigurationen für Mehrlagenverbunde",
      intro: "Lieferbar in AISI 316L und 304L mit abgestimmten Drahtstärken und Lochbildern für optimalen Durchfluss.",
      columns: [
        { key: "configuration", label: "Verbundtyp", align: "left" },
        { key: "layerRange", label: "Lagen", align: "right" },
        { key: "ratingRange", label: "Feinheit (µm)", align: "right" },
        { key: "thicknessRange", label: "Dicke (mm)", align: "right" },
        { key: "baseSupport", label: "Trägerbasis", align: "left" },
      ],
      rows: [
        {
          configuration: "Gewebe + Lochblech-Verbund",
          layerRange: "2 – 4",
          ratingRange: "5 – 200",
          thicknessRange: "1.5 – 10.0",
          baseSupport: "Starre Lochplatte (1.0 – 8.0 mm)",
        },
        {
          configuration: "6-Lagen-Schwerlastmatrix",
          layerRange: "6",
          ratingRange: "2 – 100",
          thicknessRange: "2.5 – 4.5",
          baseSupport: "Doppeltes schweres Stützgitter",
        },
        {
          configuration: "8-Lagen-Tiefenlaminat",
          layerRange: "8",
          ratingRange: "1 – 150",
          thicknessRange: "3.5 – 6.0",
          baseSupport: "Dreifaches tragendes Skelettgewebe",
        },
        {
          configuration: "Ultra-dünner 2-Lagen-Verbund",
          layerRange: "2",
          ratingRange: "10 – 100",
          thicknessRange: "0.5 – 1.2",
          baseSupport: "Einzellage Feinstützgewebe",
        },
      ],
      caption: "Alle Spezifikationen werden nach verfahrenstechnischen Differenzdruckvorgaben diffusionsgebunden gefertigt.",
    },
    features: {
      heading: "Konstruktive Leistungsmerkmale",
      intro: "Ausgelegt für anspruchsvolle Prozessbedingungen mit Fokus auf Steifigkeit und Porenkonstanz.",
      items: [
        {
          title: "Extreme mechanische Steifigkeit",
          text: "Die Festkörperverbindung mit Lochplatten verhindert Medienverformung bei plötzlichen Druckspitzen.",
        },
        {
          title: "Flexible Lagenkombination",
          text: "Kombinieren Sie Gewebearten und Drahtstärken für maßgeschneiderte Abscheidegrade und Durchflussraten.",
        },
        {
          title: "Abrieb- und Ermüdungsfestigkeit",
          text: "Der massive Lochblechträger schützt feine Filterlagen vor dynamischer Partikelerosion.",
        },
        {
          title: "Präzise Weiterverarbeitung",
          text: "Geeignet für Plasmaschneiden, Kantenbearbeitung und WIG-Schweißen zu Kerzen, Konus- und Plattenfiltern.",
        },
      ],
    },
    related: {
      heading: "Weitere Sintergewebe-Lösungen",
      intro: "Vergleichen Sie Mehrlagenverbunde mit standardisierten 3- und 5-lagigen Sinterplatten.",
      cards: [
        {
          title: "Mehrlagen-Sinterdrahtgewebe",
          text: "Die übergeordnete Produktfamilie diffusionsgebundener Schichtverbunde.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Vakuum-diffusionsgebundenes Mehrlagen-Sinterdrahtgewebelaminat",
          },
          badge: "Hauptfamilie",
          linkLabel: "Zur Mehrlagen-Übersicht",
        },
        {
          title: "3-lagiges Sintergewebe",
          text: "Aufgebaut aus Schutzlage, Filterlage und offenem Stützgewebe für hohe Durchlässigkeit.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Querschnitt eines 3-lagigen Edelstahl-Sintergewebelaminats",
          },
          badge: "3-Lagen",
          linkLabel: "3-Lagen-Details",
        },
        {
          title: "5-lagiges Standard-Sintergewebe",
          text: "Die universelle Industriekonfiguration mit Schutz-, Filter-, Dispersions- und zwei Stützlagen.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "Struktur eines 5-lagigen Standard-Sintermetallgewebes",
          },
          badge: "5-Lagen-Standard",
          linkLabel: "5-Lagen-Details",
        },
      ],
    },
    cta: {
      id: "multi-layer-sintered-cta",
      eyebrow: "Robuste Sonderanfertigung",
      heading: "Projektieren Sie Ihren Mehrlagen-Verbundfilter",
      lead: "Unser Engineering unterstützt Sie bei Schichtaufbau und Lochblechauslegung für Ihre spezifischen Druck- und Filtrationsanforderungen.",
      secondaryLabel: "Engineering anfragen",
      secondaryTo: "/ContactPage",
      note: "Werkstoffe AISI 316L und 304L mit Prüfzeugnissen gemäß Bestellung lieferbar.",
    },
  },
  fr: {
    breadcrumbKey: "MultiLayer",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Toile multicouche frittée",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toile composite multicouche frittée",
      lead: "Toile métallique multicouche frittée sous vide associée à une tôle perforée rigide ou des toiles de renfort, conçue pour résister aux pics de pression différentielle.",
      secondaryCta: {
        label: "Aperçu multicouche",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Vue macro d'un stratifié multicouche fritté en acier inoxydable",
      },
      cardNote: "Composite avec tôle perforée",
      cardLink: {
        label: "Demander les spécifications",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Seuil", value: "1 – 200 µm" },
        { label: "Nuances standard", value: "AISI 316L / 304L" },
        { label: "Nombre de couches", value: "2 à 9+ couches" },
        { label: "Épaisseur", value: "0.5 – 10.0 mm" },
      ],
    },
    overview: {
      eyebrow: "Architecture renforcée",
      heading: "Composites multicouches sur mesure renforcés par tôle perforée",
      paragraphs: [
        "La toile composite multicouche associe des toiles de filtration fines à des armatures épaisses ou à des tôles perforées solides en acier inoxydable. Conçue pour les fortes contraintes de pression et les milieux chargés, elle procure une rigidité mécanique hors pair.",
        "Soudée par diffusion à une tôle perforée, la toile filtrante est soutenue en d'innombrables points de contact métallurgiques, éliminant les risques de flexion et de fatigue sous pulsation de flux.",
        "Les réalisations sur mesure s'échelonnent de 2 à plus de 9 couches, avec des épaisseurs de 0.5 mm à 10.0 mm, assurant stabilité dimensionnelle et parfaite compatibilité avec les contre-lavages répétés.",
      ],
      keyPoints: [
        "Le frittage sous vide associe toile fine et tôles perforées rigides en un ensemble monolithique.",
        "Résiste aux chocs de pression différentielle et aux suspensions particulaires abrasives.",
        "Nombre de couches adaptable de 2 à 9+ couches pour des épaisseurs de 0.5 à 10.0 mm.",
      ],
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Vue macro d'un stratifié multicouche fritté en acier inoxydable",
      },
    },
    specTable: {
      heading: "Configurations composites multicouches",
      intro: "Nuances AISI 316L et 304L avec diamètres de fil et perforations ajustés au débit de procédé.",
      columns: [
        { key: "configuration", label: "Type de composite", align: "left" },
        { key: "layerRange", label: "Couches", align: "right" },
        { key: "ratingRange", label: "Seuil (µm)", align: "right" },
        { key: "thicknessRange", label: "Épaisseur (mm)", align: "right" },
        { key: "baseSupport", label: "Support de base", align: "left" },
      ],
      rows: [
        {
          configuration: "Toile + Tôle perforée",
          layerRange: "2 – 4",
          ratingRange: "5 – 200",
          thicknessRange: "1.5 – 10.0",
          baseSupport: "Tôle perforée rigide (1.0 – 8.0 mm)",
        },
        {
          configuration: "Matrice lourde 6 couches",
          layerRange: "6",
          ratingRange: "2 – 100",
          thicknessRange: "2.5 – 4.5",
          baseSupport: "Double armature en toile tissée lourde",
        },
        {
          configuration: "Stratifié profond 8 couches",
          layerRange: "8",
          ratingRange: "1 – 150",
          thicknessRange: "3.5 – 6.0",
          baseSupport: "Triple ossature structurale",
        },
        {
          configuration: "Stratifié fin 2 couches",
          layerRange: "2",
          ratingRange: "10 – 100",
          thicknessRange: "0.5 – 1.2",
          baseSupport: "Toile unique de soutien fin",
        },
      ],
      caption: "Toutes les configurations sont produites par diffusion selon vos contraintes de pression.",
    },
    features: {
      heading: "Avantages techniques démontrés",
      intro: "Pensée pour les environnements industriels sévères exigeant constance et robustesse prolongée.",
      items: [
        {
          title: "Rigidité mécanique exceptionnelle",
          text: "L'assemblage avec tôle perforée empêche la déformation sous fortes hausses de pression.",
        },
        {
          title: "Personnalisation intégrale",
          text: "Combinez les toiles et épaisseurs selon vos besoins précis de débit et de coupure micrométrique.",
        },
        {
          title: "Résistance à l'abrasion",
          text: "La tôle support absorbe l'énergie d'impact des fluides chargés, protégeant la couche filtrante.",
        },
        {
          title: "Excellente aptitude à l'usinage",
          text: "Se découpe au jet d'eau ou plasma et se soude en TIG pour fabriquer cartouches et éléments plats.",
        },
      ],
    },
    related: {
      heading: "Solutions frittées complémentaires",
      intro: "Comparez les composites multicouches avec nos plaques standard 3 et 5 couches.",
      cards: [
        {
          title: "Toile métallique multicouche frittée",
          text: "Gamme principale regroupant nos solutions standards et sur mesure frittées sous vide.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Stratifié de toile métallique multicouche frittée sous vide",
          },
          badge: "Gamme mère",
          linkLabel: "Voir l'aperçu multicouche",
        },
        {
          title: "Toile frittée 3 couches",
          text: "Composée d'une toile protectrice, d'une couche de filtration et d'un support pour grand débit.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Coupe transversale d'un stratifié fritté à 3 couches en acier inoxydable",
          },
          badge: "3 couches",
          linkLabel: "Détails 3 couches",
        },
        {
          title: "Toile frittée standard 5 couches",
          text: "La configuration universelle associant protection, contrôle, dispersion et double armature.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "Structure de toile métallique frittée 5 couches standard",
          },
          badge: "5 couches standard",
          linkLabel: "Détails 5 couches",
        },
      ],
    },
    cta: {
      id: "multi-layer-sintered-cta",
      eyebrow: "Ingénierie renforcée",
      heading: "Concevez votre élément multicouche renforcé",
      lead: "Nos ingénieurs vous accompagnent dans le dimensionnement des couches et le choix des tôles perforées adaptées à vos procédés.",
      secondaryLabel: "Consulter l'ingénierie",
      secondaryTo: "/ContactPage",
      note: "Nuances AISI 316L et 304L avec certificats de contrôle fournis sur commande.",
    },
  },
  pt: {
    breadcrumbKey: "MultiLayer",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Malha multicamadas sinterizada",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malha composta multicamadas sinterizada",
      lead: "Malha de arame multicamadas sinterizada a vácuo combinada com chapa perfurada rígida ou malhas de reforço, suportando picos severos de pressão diferencial.",
      secondaryCta: {
        label: "Visão geral multicamadas",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Visão macro de laminado multicamadas sinterizado de aço inoxidável",
      },
      cardNote: "Composto com chapa perfurada",
      cardLink: {
        label: "Solicitar dados técnicos",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Retenção", value: "1 – 200 µm" },
        { label: "Ligas padrão", value: "AISI 316L / 304L" },
        { label: "Camadas", value: "2 a 9+ camadas" },
        { label: "Espessura", value: "0.5 – 10.0 mm" },
      ],
    },
    overview: {
      eyebrow: "Estrutura de alta resistência",
      heading: "Compostos multicamadas sob medida com chapa perfurada",
      paragraphs: [
        "A malha composta multicamadas combina telas de retenção de precisão com telas de reforço pesadas ou chapas perfuradas sólidas de aço inoxidável. Desenvolvida para resistir a pressões dinâmicas extremas e suspensões abrasivas.",
        "Unida por difusão atômica a uma chapa perfurada, a malha fina fica ancorada em milhares de pontos de contato, evitando flexão e quebra por fadiga sob ciclos contínuos de fluxo e retrolavagem.",
        "As configurações variam de 2 a mais de 9 camadas com espessuras de 0.5 mm a 10.0 mm, assegurando retenção estável e longa vida útil sob operação contínua.",
      ],
      keyPoints: [
        "A sinterização a vácuo une telas finas e chapas perfuradas sólidas em uma peça única.",
        "Resiste a picos severos de pressão diferencial e meios com partículas altamente abrasivas.",
        "Número de camadas de 2 a 9+ com espessuras totais de placa entre 0.5 e 10.0 mm.",
      ],
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Visão macro de laminado multicamadas sinterizado de aço inoxidável",
      },
    },
    specTable: {
      heading: "Configurações de compostos multicamadas",
      intro: "Disponíveis em aços inoxidáveis AISI 316L e 304L com furações e malhas customizadas.",
      columns: [
        { key: "configuration", label: "Tipo de composto", align: "left" },
        { key: "layerRange", label: "Camadas", align: "right" },
        { key: "ratingRange", label: "Retenção (µm)", align: "right" },
        { key: "thicknessRange", label: "Espessura (mm)", align: "right" },
        { key: "baseSupport", label: "Suporte de base", align: "left" },
      ],
      rows: [
        {
          configuration: "Tela + Chapa perfurada",
          layerRange: "2 – 4",
          ratingRange: "5 – 200",
          thicknessRange: "1.5 – 10.0",
          baseSupport: "Chapa perfurada rígida (1.0 – 8.0 mm)",
        },
        {
          configuration: "Matriz pesada de 6 camadas",
          layerRange: "6",
          ratingRange: "2 – 100",
          thicknessRange: "2.5 – 4.5",
          baseSupport: "Dupla malha tecida de suporte",
        },
        {
          configuration: "Laminado profundo de 8 camadas",
          layerRange: "8",
          ratingRange: "1 – 150",
          thicknessRange: "3.5 – 6.0",
          baseSupport: "Esqueleto estrutural triplo",
        },
        {
          configuration: "Laminado fino de 2 camadas",
          layerRange: "2",
          ratingRange: "10 – 100",
          thicknessRange: "0.5 – 1.2",
          baseSupport: "Tela única de suporte fino",
        },
      ],
      caption: "Todas as configurações são sinterizadas sob medida conforme a pressão diferencial exigida.",
    },
    features: {
      heading: "Diferenciais de engenharia",
      intro: "Projetada para processos pesados que exigem integridade estrutural e retenção constante.",
      items: [
        {
          title: "Rigidez estrutural extrema",
          text: "A fusão atômica com chapas perfuradas impede a deformação sob oscilações repentinas de pressão.",
        },
        {
          title: "Ampla flexibilidade de camadas",
          text: "Combine diferentes malhas e espessuras para atingir a vazão e o grau de filtragem requeridos.",
        },
        {
          title: "Resistência a meios abrasivos",
          text: "A chapa base protege as camadas finas contra desgaste prematuro por impacto de sólidos.",
        },
        {
          title: "Facilidade de conformação",
          text: "Pode ser cortada a plasma, usinada nas bordas e soldada em TIG para formar cilindros e discos.",
        },
      ],
    },
    related: {
      heading: "Outras opções em telas sinterizadas",
      intro: "Compare os compostos reforçados com os modelos padronizados de 3 e 5 camadas.",
      cards: [
        {
          title: "Malha multicamadas sinterizada",
          text: "Família principal que engloba estruturas sinterizadas de 3 e 5 camadas e modelos especiais.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Laminado de malha de arame multicamadas sinterizado a vácuo",
          },
          badge: "Linha principal",
          linkLabel: "Ver visão geral multicamadas",
        },
        {
          title: "Malha sinterizada de 3 camadas",
          text: "Desenvolvida com malha de proteção, controle e suporte para alta permeabilidade e vazão.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Corte transversal de malha sinterizada de 3 camadas em aço inoxidável",
          },
          badge: "3 camadas",
          linkLabel: "Ver detalhes de 3 camadas",
        },
        {
          title: "Malha sinterizada padrão de 5 camadas",
          text: "A configuração industrial universal que combina proteção, controle, dispersão e dois reforços estruturais.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "Estrutura de malha sinterizada padrão de 5 camadas",
          },
          badge: "5 camadas padrão",
          linkLabel: "Ver detalhes de 5 camadas",
        },
      ],
    },
    cta: {
      id: "multi-layer-sintered-cta",
      eyebrow: "Projetos de alta robustez",
      heading: "Desenvolva sua malha composta reforçada",
      lead: "Nossa engenharia orienta a escolha das telas e espessuras de chapas perfuradas ideais para seu processo.",
      secondaryLabel: "Falar com engenharia",
      secondaryTo: "/ContactPage",
      note: "Disponível nas ligas AISI 316L e 304L com certificados emitidos conforme pedido.",
    },
  },
};
